import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Briefcase, Check, MessageSquare, Phone, RotateCcw, Send, X } from 'lucide-react';
import { api, ApiError } from '../api';
import { Shell } from '../components/layout/Shell';
import { PageHeader } from '../components/layout/PageHeader';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { EmptyState } from '../components/ui/empty-state';
import { useToast } from '../lib/toast';
import { fmtRelative } from '../lib/utils';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';

interface AdminSupportMessage {
  id: string;
  kind: 'feedback' | 'enterprise';
  company: string | null;
  body: string;
  status: 'open' | 'resolved';
  created_at: string;
  resolved_at: string | null;
  resolved_by: string | null;
  user_id: string;
  user_phone: string;
  user_name: string | null;
  user_telegram_username: string | null;
}

export function SupportPage() {
  const [params, setParams] = useSearchParams();
  const status = params.get('status') ?? 'open';
  const kind = params.get('kind') ?? '';

  const [rows, setRows] = useState<AdminSupportMessage[] | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const { show: toast } = useToast();

  function patch(next: Record<string, string>) {
    const merged = new URLSearchParams(params);
    for (const [k, v] of Object.entries(next)) {
      if (v == null || v === '') merged.delete(k);
      else merged.set(k, v);
    }
    setParams(merged);
  }

  function load() {
    setRows(null);
    setErr(null);
    const qs = new URLSearchParams();
    if (status) qs.set('status', status);
    if (kind) qs.set('kind', kind);
    api<{ messages: AdminSupportMessage[] }>(
      `/api/admin/support${qs.toString() ? `?${qs}` : ''}`,
    )
      .then((r) => setRows(r.messages))
      .catch((e) => setErr(e instanceof ApiError ? `Error ${e.status}` : 'Could not load'));
  }

  useEffect(load, [status, kind]);

  async function setStatus(id: string, next: 'open' | 'resolved') {
    setBusyId(id);
    try {
      await api(`/api/admin/support/${id}`, {
        method: 'PATCH',
        json: { status: next },
      });
      toast(next === 'resolved' ? 'Marked resolved.' : 'Reopened.', 'success');
      load();
    } catch (e) {
      toast(
        e instanceof ApiError ? `Error ${e.status}` : 'Update failed.',
        'error',
      );
    } finally {
      setBusyId(null);
    }
  }

  const hasFilters = status !== 'open' || Boolean(kind);

  return (
    <Shell title="Support">
      <PageHeader
        title="Support"
        description="In-app feedback and enterprise enquiries from /settings."
      />

      <Card className="mb-4">
        <CardContent className="flex flex-col gap-3 p-4 md:flex-row md:items-center md:gap-2">
          <div className="grid grid-cols-2 gap-2 md:flex md:gap-2">
            <Select
              value={status || 'open'}
              onValueChange={(v) => patch({ status: v === 'all' ? '' : v })}
            >
              <SelectTrigger className="md:w-36">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="open">Open</SelectItem>
                <SelectItem value="resolved">Resolved</SelectItem>
                <SelectItem value="all">All</SelectItem>
              </SelectContent>
            </Select>
            <Select
              value={kind || 'any'}
              onValueChange={(v) => patch({ kind: v === 'any' ? '' : v })}
            >
              <SelectTrigger className="md:w-44">
                <SelectValue placeholder="Kind" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="any">Any kind</SelectItem>
                <SelectItem value="feedback">Feedback</SelectItem>
                <SelectItem value="enterprise">Enterprise</SelectItem>
              </SelectContent>
            </Select>
            {hasFilters && (
              <Button
                variant="ghost"
                size="sm"
                className="md:h-9"
                onClick={() => setParams(new URLSearchParams())}
              >
                <X className="h-3.5 w-3.5" /> Reset
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {err && (
        <Card className="mb-4 border-error/40 bg-error-tint">
          <CardContent className="py-3 text-sm text-error-on-tint">{err}</CardContent>
        </Card>
      )}

      <div className="space-y-3">
        {!rows &&
          Array.from({ length: 5 }).map((_, i) => (
            <Card key={i}>
              <CardContent className="p-4">
                <Skeleton className="h-20 w-full" />
              </CardContent>
            </Card>
          ))}

        {rows && rows.length === 0 && (
          <Card>
            <CardContent>
              <EmptyState
                icon={Send}
                title={hasFilters ? 'No messages match these filters.' : 'No support messages yet.'}
              />
            </CardContent>
          </Card>
        )}

        {rows?.map((m) => {
          const KindIcon = m.kind === 'enterprise' ? Briefcase : MessageSquare;
          const waHref = `https://wa.me/${m.user_phone.replace(/^\+/, '')}`;
          return (
            <Card key={m.id}>
              <CardContent className="flex flex-col gap-3 p-4 md:flex-row md:items-start">
                <div className="flex shrink-0 flex-col items-start gap-1 md:w-44">
                  <Badge variant={m.kind === 'enterprise' ? 'brand' : 'default'}>
                    <KindIcon className="mr-1 h-3 w-3" />
                    {m.kind === 'enterprise' ? 'Enterprise' : 'Feedback'}
                  </Badge>
                  {m.status === 'resolved' && (
                    <Badge variant="completed">Resolved</Badge>
                  )}
                  {m.kind === 'enterprise' && m.company && (
                    <p className="mt-1 text-xs font-medium text-text">{m.company}</p>
                  )}
                </div>
                <div className="min-w-0 flex-1">
                  <p className="whitespace-pre-wrap text-sm text-text">{m.body}</p>
                  <p className="mt-2 text-xs text-text-muted">
                    <Link
                      to={`/users/${m.user_id}`}
                      className="hover:text-brand-700 dark:hover:text-brand-500"
                    >
                      {m.user_name ?? m.user_phone}
                    </Link>
                    {m.user_telegram_username ? ` · @${m.user_telegram_username}` : ''}
                    {' · '}
                    {fmtRelative(m.created_at)}
                  </p>
                </div>
                <div className="flex shrink-0 flex-col gap-2 md:items-end">
                  <a
                    href={waHref}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1.5 rounded-md border border-border bg-surface px-3 py-1.5 text-xs font-semibold text-text hover:bg-surface-muted"
                  >
                    <Phone className="h-3.5 w-3.5" />
                    WhatsApp
                  </a>
                  {m.status === 'open' ? (
                    <Button
                      variant="secondary"
                      size="sm"
                      disabled={busyId === m.id}
                      onClick={() => setStatus(m.id, 'resolved')}
                    >
                      <Check className="h-3.5 w-3.5" /> Mark resolved
                    </Button>
                  ) : (
                    <Button
                      variant="ghost"
                      size="sm"
                      disabled={busyId === m.id}
                      onClick={() => setStatus(m.id, 'open')}
                    >
                      <RotateCcw className="h-3.5 w-3.5" /> Reopen
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>
    </Shell>
  );
}
